
import { useState } from "react";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { CircleDollarSign, Plus } from "lucide-react";

interface GroupCreatorProps {
  onSuccess?: () => void;
}

export const GroupCreator = ({ onSuccess }: GroupCreatorProps) => {
  const { t } = useTranslation();
  const [groupName, setGroupName] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupName.trim()) {
      toast({
        title: t('common.error'),
        description: t('groups.createGroupError'),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: t('common.error'),
          description: t('groups.createGroupError'),
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase.rpc('create_group_with_admin', {
        group_name: groupName,
        creator_id: session.user.id
      });

      if (error) throw error;

      toast({
        title: t('common.success'),
        description: t('groups.createGroupSuccess'),
        className: "bg-gradient-to-r from-green-500 to-emerald-500 text-white",
      });

      // Clear form and call onSuccess callback
      setGroupName("");
      onSuccess?.();
    } catch (error) {
      console.error('Error creating group:', error);
      toast({
        title: t('common.error'),
        description: t('groups.createGroupError'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="backdrop-blur-sm bg-gradient-to-br from-primary/5 to-green/10 border border-primary/10">
      <CardContent className="p-6">
        <form onSubmit={handleCreateGroup} className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <CircleDollarSign className="h-5 w-5 text-primary" />
              <CardTitle gradient>{t('groups.create')}</CardTitle>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('groups.enterGroupName')}
            </p>
          </div>
          <div className="space-y-4">
            <Input
              placeholder={t('groups.groupName')}
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              disabled={loading}
              className="border-primary/20 focus-visible:ring-primary/30"
            />
            <div className="flex justify-end gap-2">
              <Button
                type="submit"
                disabled={loading}
                variant="gradient"
                rounded="xl"
                className="shadow-md"
              >
                <Plus className="h-4 w-4" />
                {loading ? t('groups.creating') : t('groups.create')}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
